// src/utils/notificationRules.ts
//
// Suy ra danh sách thông báo từ trạng thái thật trong DB - phần LÕI, thuần tuý.
//
// Vì sao tách khỏi hook: cùng một danh sách này giờ có HAI nơi cần dựng.
//
//   1. trong trình duyệt  -> chuông thông báo (hooks/useNotifications,
//                            hooks/useTenantNotifications);
//   2. ngoài trình duyệt  -> bộ gửi push chạy trên Cloud Functions
//                            (functions/src/notify.ts). Nó bắn NGAY khi dữ liệu
//                            đổi nhờ 5 trigger RTDB, cộng một lượt quét 8 giờ
//                            sáng cho thứ không sự kiện nào sinh ra được:
//                            "chưa ghi số điện nước".
//
// Nếu để logic nằm trong hook thì bộ gửi buộc phải chép lại, và hai bản chép sẽ
// lệch nhau ngay lần sửa đầu tiên - lúc đó chuông báo một đằng, điện thoại báo
// một nẻo. Nên mọi thứ ở đây KHÔNG import React, không import firebase, không
// đọc Date.now() hay localStorage: đưa dữ liệu vào, nhận danh sách ra. Nhờ vậy
// bộ gửi chạy được bằng node, và sau này chuyển sang Cloudflare Workers cũng
// không phải viết lại tí logic nào.
//
// Quy ước còn giữ nguyên: KHÔNG có node `notifications` trong DB. Mọi mục đều
// suy ra, nên không bao giờ lệch với thực tế và không ai phải đi dọn.

import {AppNotification} from '../types/notification';
import {Room} from '../types/room';
import {UtilityRecord} from '../types/utilityRecord';
import {formatCurrency, formatYearMonth, needsUtilityEntry} from './helpers';

/** Cũ hơn mốc này thì thôi báo - thông báo về việc của 2 tháng trước là rác */
export const RECENT_MS = 30 * 24 * 60 * 60 * 1000;

const parseAt = (iso?: string): number | undefined => {
    if (!iso) return undefined;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? undefined : ms;
};

/* ------------------------------------------------------------------ */
/*  Phía chủ nhà: việc đang chờ mình xử lý                             */
/* ------------------------------------------------------------------ */

export interface LandlordSources {
    /** roomDirectory - node nhẹ, chỉ cần `name` để hiển thị */
    rooms: Record<string, { name?: string } | null>;
    /** roomId -> bản ghi điện nước của THÁNG HIỆN TẠI */
    records: Record<string, UtilityRecord | null>;
    registrations: Record<string, any>;
    handovers: Record<string, any>;
    emailChanges: Record<string, any>;
}

export const deriveLandlordNotifications = (sources: LandlordSources): AppNotification[] => {
    const {rooms, records, registrations, handovers, emailChanges} = sources;
    const roomName = (roomId: string) => rooms[roomId]?.name || `Phòng ${roomId}`;
    const items: AppNotification[] = [];

    Object.entries(records).forEach(([roomId, record]) => {
        if (!record) return;
        // Trỏ thẳng vào TRANG của phòng đó. Chỉ trỏ về /diennuoc thì chủ nhà
        // đọc xong thông báo vẫn phải tự đi dò phòng nào trong lưới vài chục
        // thẻ - làm lại từ đầu đúng cái việc mà thông báo vừa nói.
        if (record.paymentStatus === 'landlord_pending') {
            items.push({
                id: `review-${roomId}`,
                kind: 'review',
                title: 'Chờ duyệt số điện nước',
                detail: roomName(roomId),
                to: `/diennuoc/${roomId}`,
            });
        }
        if (record.paymentStatus === 'payment_submitted') {
            items.push({
                id: `payment-${roomId}`,
                kind: 'payment',
                title: 'Đã báo chuyển khoản',
                detail: roomName(roomId),
                to: `/diennuoc/${roomId}`,
                at: parseAt(record.paymentSubmittedAt),
            });
        }
    });

    Object.entries(registrations).forEach(([key, reg]: [string, any]) => {
        if (reg?.status !== 'pending') return;
        items.push({
            id: `reg-${key}`,
            kind: 'registration',
            title: 'Yêu cầu đăng ký mới',
            detail: `${reg.name || reg.email} → ${roomName(reg.roomId)}`,
            to: '/phanquyen',
            at: reg.createdAt,
        });
    });

    Object.entries(handovers).forEach(([roomId, request]: [string, any]) => {
        items.push({
            id: `handover-${roomId}`,
            kind: 'handover',
            title: 'Yêu cầu dọn hồ sơ người ở trước',
            detail: `${roomName(roomId)} · ${request?.requestedByName || request?.requestedBy || ''}`,
            to: `/phong/${roomId}`,
            at: request?.requestedAt,
        });
    });

    Object.entries(emailChanges).forEach(([key, request]: [string, any]) => {
        items.push({
            id: `email-${key}`,
            kind: 'emailChange',
            title: 'Yêu cầu đổi email đăng nhập',
            detail: `${request?.name || request?.currentEmail || ''} → ${request?.newEmail || ''}`,
            to: '/phanquyen',
            at: request?.requestedAt,
        });
    });

    // Mới nhất lên trước; mục không có mốc thời gian xuống cuối
    return items.sort((a, b) => (b.at ?? 0) - (a.at ?? 0));
};

/* ------------------------------------------------------------------ */
/*  Phía người thuê: chủ nhà vừa làm gì với phòng mình                 */
/* ------------------------------------------------------------------ */

export interface TenantSources {
    room: Room | null;
    /** yearMonth -> bản ghi. Chỉ cần tháng này và tháng trước. */
    records: Record<string, UtilityRecord | null>;
    thisMonth: string;
    prevMonth: string;
    /** Truyền vào thay vì gọi Date.now() để bộ gửi chạy được tất định */
    now: number;
}

export const deriveTenantNotifications = (sources: TenantSources): AppNotification[] => {
    const {room, records, thisMonth, prevMonth, now} = sources;
    if (!room) return [];

    const items: AppNotification[] = [];
    const isRecent = (at?: number) => at === undefined || now - at < RECENT_MS;

    [thisMonth, prevMonth].forEach(month => {
        const record = records[month];
        const label = `Tháng ${formatYearMonth(month)}`;

        // Chưa có số tháng này - nhắc ghi. Chỉ nhắc tháng hiện tại: tháng
        // trước đã trôi qua, nhắc nữa cũng không ghi được.
        if (month === thisMonth
            && needsUtilityEntry(room)
            && (!record || record.paymentStatus === 'waiting_tenant_input')) {
            items.push({
                id: `remind-${month}`,
                kind: 'remind',
                title: 'Chưa ghi số điện nước',
                detail: `${label} · ghi số đồng hồ để chủ nhà chốt hóa đơn`,
            });
            return;
        }

        if (!record) return;

        switch (record.paymentStatus) {
            case 'landlord_rejected': {
                const at = parseAt(record.rejectedAt);
                if (!isRecent(at)) break;
                // Lý do đi THEO thông báo, không chỉ nằm trong app: thông báo đẩy
                // là thứ người thuê đọc trên màn khoá, và "kiểm tra lại đồng hồ"
                // không nói được họ phải sửa cái gì. Cắt ngắn vì cả tiêu đề lẫn
                // nội dung push đều bị hệ điều hành cắt sau vài chục ký tự.
                const reason = record.rejectReason?.trim();
                items.push({
                    id: `rejected-${month}-${at ?? ''}`,
                    kind: 'rejected',
                    title: 'Chủ nhà đã từ chối số điện nước',
                    detail: reason
                        ? `${label} · ${reason.length > 80 ? `${reason.slice(0, 79)}…` : reason}`
                        : `${label} · kiểm tra lại đồng hồ rồi ghi lại`,
                    at,
                });
                break;
            }
            case 'waiting_payment': {
                const at = parseAt(record.approvedAt);
                if (!isRecent(at)) break;
                items.push({
                    id: `invoice-${month}-${at ?? ''}`,
                    kind: 'invoice',
                    title: 'Chủ nhà đã chốt hóa đơn',
                    detail: `${label} · cần thanh toán ${formatCurrency(record.total ?? 0)}`,
                    at,
                });
                break;
            }
            case 'paid':
            case 'deducted_deposit': {
                // Bắt buộc có mốc: bản ghi cũ không có paidAt mà vẫn báo thì
                // tháng nào mở app cũng thấy "đã thu tiền" của đời nào.
                const at = parseAt(record.paidAt);
                if (at === undefined || !isRecent(at)) break;
                const fromDeposit = record.paymentStatus === 'deducted_deposit';
                items.push({
                    id: `paid-${month}-${at}`,
                    kind: 'paid',
                    title: fromDeposit ? 'Đã trừ vào tiền cọc' : 'Chủ nhà đã xác nhận nhận tiền',
                    detail: fromDeposit
                        ? `${label} · ${formatCurrency(record.total ?? 0)} - trừ vào cọc khi trả phòng`
                        : `${label} · ${formatCurrency(record.total ?? 0)} - xong tháng này`,
                    at,
                });
                break;
            }
            default:
                // landlord_pending / payment_submitted: đang chờ CHỦ NHÀ làm,
                // không phải việc chủ nhà đã làm - trang chính đã nói rồi.
                break;
        }
    });

    if (room.status === 'moving_out' && room.moveOutMonth) {
        items.push({
            id: `moveout-${room.moveOutMonth}`,
            kind: 'moveOut',
            title: 'Chủ nhà đã ghi nhận bạn trả phòng',
            detail: `Tháng cuối cần thanh toán: tháng ${formatYearMonth(room.moveOutMonth)}`,
            at: room.moveOutNoticedAt,
        });
    }

    // Mới nhất lên trước. Mục không có mốc (nhắc ghi số, trả phòng) là việc
    // đang cần làm ngay - cho lên đầu chứ không dồn xuống đáy.
    return items.sort((a, b) => (b.at ?? Number.MAX_SAFE_INTEGER) - (a.at ?? Number.MAX_SAFE_INTEGER));
};
